const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export interface PitchResult {
  hz: number;
  note: string;
  octave: number;
  cents: number;
  clarity: number;
}

export function hzToNoteInfo(hz: number): Omit<PitchResult, 'clarity'> {
  const midi = 12 * Math.log2(hz / 440) + 69;
  const roundedMidi = Math.round(midi);
  const cents = Math.round((midi - roundedMidi) * 100);
  const octave = Math.floor(roundedMidi / 12) - 1;
  const noteIndex = ((roundedMidi % 12) + 12) % 12;
  return {
    hz: Math.round(hz * 10) / 10,
    note: NOTE_NAMES[noteIndex],
    octave,
    cents,
  };
}

// YIN pitch detection — far more robust against octave/harmonic errors than
// plain autocorrelation. Finds the fundamental period using a difference
// function + cumulative mean normalization (CMNDF), then takes the first dip
// below a threshold rather than the global maximum.
//
// de Cheveigné & Kawahara (2002) — JASA 111(4):1917-1930
export function detectPitch(buffer: Float32Array, sampleRate: number): { hz: number; clarity: number } | null {
  const SIZE = Math.min(buffer.length, 2048);
  const HALF = SIZE >> 1;              // number of pairs we correlate

  // Reject silence / breath noise early
  let rms = 0;
  for (let i = 0; i < SIZE; i++) rms += buffer[i] * buffer[i];
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.003) return null;

  // Lag range corresponding to 55 – 3000 Hz
  const lagMin = Math.max(2, Math.floor(sampleRate / 3000)); // ~15 samples @ 44100
  const lagMax = Math.min(HALF - 1, Math.floor(sampleRate / 55)); // ~802 samples

  // Step 1: Difference function
  //   d(tau) = Σ (x[i] − x[i+tau])²
  const diff = new Float32Array(lagMax + 1);
  for (let tau = lagMin; tau <= lagMax; tau++) {
    let sum = 0;
    for (let i = 0; i < HALF; i++) {
      const delta = buffer[i] - buffer[i + tau];
      sum += delta * delta;
    }
    diff[tau] = sum;
  }

  // Step 2: Cumulative mean normalized difference function (CMNDF)
  //   cmndf(tau) = d(tau) / ((1/tau) Σ_{j=1}^{tau} d(j))
  //   = 1 at tau=0 by definition
  const cmndf = new Float32Array(lagMax + 1);
  cmndf[0] = 1;
  let cumSum = 0;
  for (let tau = 1; tau <= lagMax; tau++) {
    const d = tau >= lagMin ? diff[tau] : 0;
    cumSum += d;
    cmndf[tau] = cumSum > 0 ? (d * tau) / cumSum : 1;
  }

  // Step 3: First dip below threshold — this is the fundamental period.
  // Descend to the local minimum of the dip before accepting it.
  const THRESHOLD = 0.12;
  let bestLag = -1;
  for (let tau = lagMin; tau <= lagMax; tau++) {
    if (cmndf[tau] < THRESHOLD) {
      while (tau + 1 <= lagMax && cmndf[tau + 1] < cmndf[tau]) tau++;
      bestLag = tau;
      break;
    }
  }

  // Fallback: no dip below threshold — use global minimum, but reject if unclear
  if (bestLag === -1) {
    let minVal = Infinity;
    for (let tau = lagMin; tau <= lagMax; tau++) {
      if (cmndf[tau] < minVal) {
        minVal = cmndf[tau];
        bestLag = tau;
      }
    }
    if (minVal > 0.30) return null;
  }

  // Step 4: Parabolic interpolation for sub-sample accuracy
  // For a minimum: refined = tau + (x0 − x2) / (2*(x0 + x2 − 2*x1))
  let refinedLag = bestLag;
  if (bestLag > lagMin && bestLag < lagMax) {
    const x0 = cmndf[bestLag - 1];
    const x1 = cmndf[bestLag];
    const x2 = cmndf[bestLag + 1];
    const denom = x0 + x2 - 2 * x1;
    if (denom > 1e-10) refinedLag = bestLag + (x0 - x2) / (2 * denom);
  }

  const hz = sampleRate / refinedLag;
  if (hz < 55 || hz > 3000) return null;

  // Clarity: 1 = perfectly periodic, 0 = noise (CMNDF near 0 = very periodic)
  const clarity = Math.max(0, Math.min(1, 1 - cmndf[bestLag]));
  return { hz, clarity };
}
